/** The engine's data (docs/spec/02, 03, 05 §4). */

import type { Capsule, CapsuleKind, Bomb, Enemy, EnemyKind, AfterPath, Shot } from './entities';
import type { SimEvent } from './events';
import type { Path } from './path';
import type { Rng } from './rng';

export type * from './entities';
export type * from './events';

/** What the player is holding this tick. The engine finds the presses itself. */
export interface Input {
  readonly left: boolean;
  readonly right: boolean;
  readonly fire: boolean;
  /** The Nova bomb (Supernova). */
  readonly bomb: boolean;
}

export const NO_INPUT: Input = { left: false, right: false, fire: false, bomb: false };

export type Mode = 'classic' | 'supernova';

export interface GameConfig {
  readonly mode: Mode;
  readonly seed: number;
  /** First stage, 1 or more (default 1). */
  readonly startStage?: number;
  /** Fighters to start with, counting the one flying (default 3). */
  readonly fighters?: number;
}

export type PlayerStatus =
  /** Answering the stick. */
  | 'flying'
  /** Exploded; waiting for the next ship. */
  | 'dead'
  /** The next ship is on the pad: READY. It can move and fire. */
  | 'arriving'
  /** Rising in a beam. */
  | 'caught';

export interface Player {
  /** Centre of the ship, or of the pair when dual. */
  x: number;
  status: PlayerStatus;
  /** Ticks in `status`. */
  timer: number;
  dual: boolean;
  /** Ticks of safety left (after a shield bursts, a Nova). */
  safe: number;
  fireHeld: boolean;
  bombHeld: boolean;
  /** Ticks until a held button fires again (Rapid). */
  cooldown: number;
  /** While caught: where the ship is on its way up, and how far it has turned. */
  caughtX: number;
  caughtY: number;
  spin: number;
}

/** Supernova's power-ups: ticks left for the timed ones. */
export interface Powers {
  rapid: number;
  spread: number;
  pierce: number;
  chrono: number;
  shield: boolean;
  novas: number;
}

export type StageKind = 'formation' | 'challenge' | 'empress';

export type Phase = 'intro' | 'entering' | 'battle' | 'challenge' | 'tally' | 'empress' | 'clear' | 'gameover';

export interface Formation {
  /** Ticks since the stage began. */
  t: number;
  sway: number;
  spread: number;
  /** Every wave is in: the formation breathes. */
  settled: boolean;
  /** Ticks since it settled. */
  settledT: number;
}

/** One enemy waiting to enter. */
export interface Spawn {
  /** Ticks after the wave starts. */
  readonly at: number;
  readonly kind: EnemyKind;
  readonly variant: number;
  readonly path: Path;
  readonly side: number;
  /** Its place in the formation, if it has one. */
  readonly slotted: boolean;
  readonly row: number;
  readonly col: number;
  readonly after: AfterPath;
}

export interface Spawner {
  /** Waves still to come, in order. */
  pending: Spawn[][];
  /** The wave now entering; empty between waves. */
  queue: Spawn[];
  /** Waves started so far. */
  wave: number;
  /** Ticks since this wave began. */
  t: number;
  /** Ticks the spawner has waited for the wave before to clear. */
  wait: number;
}

export interface Director {
  /** Ticks until the next launch. */
  timer: number;
  /** Whose turn: 0 Stinger, 1 Moth, 2 Monarch. */
  turn: number;
  nextGroup: number;
  nextTrio: number;
  /** Trios that can no longer pay their bonus (one got away). */
  brokenTrios: number[];
}

export type BeamPhase = 'extend' | 'hold' | 'retract';

export interface Beam {
  /** The Monarch's id. */
  readonly boss: number;
  phase: BeamPhase;
  t: number;
  /** The top of the cone. */
  x: number;
  y: number;
  /** 0-1: how far the cone reaches. */
  reach: number;
}

export interface Rescue {
  x: number;
  y: number;
  t: number;
  phase: 'spin' | 'fly';
  spin: number;
  /** Which side of the fighter it is making for (-1 left, 1 right), chosen once as it sets off; 0 before. */
  side: number;
}

export type EmpressAttack = 'idle' | 'fan' | 'swarm' | 'lance';

export interface Empress {
  x: number;
  y: number;
  hp: number;
  readonly maxHp: number;
  /** Which appearance, 1 or more. */
  readonly rank: number;
  state: 'entering' | 'fighting' | 'dying';
  phase: 1 | 2;
  /** Ticks in `state`; her drift runs on its own clock so it can speed up. */
  t: number;
  drift: number;
  attack: EmpressAttack;
  attackT: number;
  /** Next attack in the rotation. */
  turn: number;
  /** Where the lance burns (x), while it is warning or firing. */
  lanceX: number;
  lance: 'none' | 'warn' | 'fire';
  /** Ticks left of the white flash after a hit (drawing only). */
  flash: number;
}

export interface Challenge {
  /** Which challenging stage this is, 1 or more. */
  readonly index: number;
  hits: number;
  /** Hits per wave. */
  readonly waveHits: number[];
  bonus: number;
  perfect: boolean;
}

export interface Stats {
  shots: number;
  hits: number;
  bestChain: number;
}

/** Everything about a running game. Presentation reads it and never writes it. */
export interface GameState {
  readonly config: GameConfig;
  readonly mode: Mode;
  readonly rng: Rng;
  /** Ticks completed. */
  tick: number;
  stage: number;
  stageKind: StageKind;
  phase: Phase;
  /** Ticks in `phase`. */
  phaseT: number;
  score: number;
  /** Fighters in reserve. */
  lives: number;
  /** The score that earns the next bonus fighter. */
  nextLifeAt: number;
  readonly player: Player;
  readonly powers: Powers;
  readonly formation: Formation;
  readonly spawner: Spawner;
  readonly director: Director;
  enemies: Enemy[];
  shots: Shot[];
  bombs: Bomb[];
  capsules: Capsule[];
  beam: Beam | null;
  rescue: Rescue | null;
  empress: Empress | null;
  challenge: Challenge | null;
  /** Supernova: kills in a row, and ticks left to add another. */
  chain: number;
  chainClock: number;
  /** Supernova: the drop meter (03 §2) and the last capsule kind drawn. */
  dropMeter: number;
  lastCapsule: CapsuleKind | null;
  /** Challenging stages and Empresses met so far. */
  challenges: number;
  empresses: number;
  /** Ticks left of FIGHTER CAPTURED on screen. */
  capturedMsg: number;
  nextId: number;
  nextVolley: number;
  readonly stats: Stats;
  /** This tick's events, replaced every step. */
  readonly events: SimEvent[];
}
